import { useCallback } from "react";
import { useForm, type UseFormProps, type UseFormReturn } from "react-hook-form";
import { focusFirstInvalidField } from "@/lib/utils";

/**
 * Custom hook that wraps react-hook-form's useForm and provides
 * a handleSubmit function that automatically focuses the first invalid field.
 * 
 * @example
 * ```tsx
 * const { handleSubmit, register, formState: { errors } } = useFormWithFocus<FormData>({
 *   resolver: zodResolver(schema),
 * });
 * 
 * <form onSubmit={handleSubmit(onSubmit)}>
 *   ...
 * </form>
 * ```
 */
export function useFormWithFocus<T extends Record<string, any>>(
  props?: UseFormProps<T>
): UseFormReturn<T> & { handleSubmit: UseFormReturn<T>["handleSubmit"] } {
  const form = useForm<T>(props);

  // Create a wrapper for handleSubmit that focuses first invalid field
  const handleSubmit = useCallback(
    (onValid?: Parameters<UseFormReturn<T>["handleSubmit"]>[0], onInvalid?: Parameters<UseFormReturn<T>["handleSubmit"]>[1]) => {
      return form.handleSubmit(
        onValid || (() => {}),
        (errors) => {
          // Focus the first invalid field
          focusFirstInvalidField(errors);
          // Call the original onInvalid if provided
          if (onInvalid) {
            onInvalid(errors);
          }
        }
      );
    },
    [form]
  );

  return {
    ...form,
    handleSubmit,
  };
}

